import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import DateCalendarInput from "app/pages/SmartDP/Search/DateCalendarInput/DateCalendarInput";
import { fetchSDPAvailableDepartureDate } from "app/pages/SmartDP/smartDPActionCreators";
import { useCallback, useContext, useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import min from "lodash/min";
import max from "lodash/max";
import maxBy from "lodash/maxBy";
import { addDays, format, isWithinInterval } from "date-fns";
import { useField, useFormikContext } from "formik";
import { HTTP_STATUS_CODES, SDP_ROTATION_DATE_FORMAT } from "app/constants";
import add from "date-fns/add";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import DateCalendarDrawerInput from "app/pages/SmartDP/Search/DateCalendarInput/DateCalendarDrawerInput";

const MIN_CALENDAR_DATE_OFFSET = 2;
const MAX_CALENDAR_DATE_OFFSET = 340;
const MIN_BOOKING_DATE_OFFSET = 3;
const MAX_BOOKING_DATE_OFFSET = 28;
const SmartDPDateCalendarContainer = props => {
	const {
		uri,
		fetchSDPAvailableDepartureDate,
		updateFloatingPositionReference = () => {},
		handleDayChange = () => {},
		popperOffset = [],
		durationByAvailableDate = {},
	} = props;
	const [field] = useField(props);

	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const selectedDepartureDate = field.value?.departureDate;
	const selectedEndDate = field.value?.endDate;

	const { setFieldValue, setFieldTouched } = useFormikContext();

	const [isCalendarAvailableDateError, setIsCalendarAvailableDateError] = useState(false);

	const today = new Date();

	const sdpDefaultCalendarMinDate = format(
		addDays(today, MIN_CALENDAR_DATE_OFFSET),
		SDP_ROTATION_DATE_FORMAT
	);
	const sdpDefaultCalendarMaxDate = format(
		addDays(today, MAX_CALENDAR_DATE_OFFSET),
		SDP_ROTATION_DATE_FORMAT
	);

	const availableDates = Object.keys(durationByAvailableDate);

	const calendarMinDate =
		availableDates.length > 0 && !isCalendarAvailableDateError
			? min(availableDates)
			: sdpDefaultCalendarMinDate;
	let calendarMaxDate =
		availableDates.length > 0 && !isCalendarAvailableDateError
			? max(availableDates)
			: sdpDefaultCalendarMaxDate;

	if (selectedDepartureDate && !isEmpty(durationByAvailableDate)) {
		// Pour afficher le mois de fin du calendrier afin que le user puisse sélectionner
		// la date de fin la plus lointaine possible pour la date de départ sélectionnée
		// Cela permet aussi de ne pas afficher les mois non utiles pour la sélection de la date de retour
		const formattedSelectedDepartureDate = format(
			selectedDepartureDate,
			SDP_ROTATION_DATE_FORMAT
		);

		// update duration from selected departureDate
		const durationsOfSelectedDepartureDate =
			durationByAvailableDate[formattedSelectedDepartureDate];

		if (!selectedEndDate && durationsOfSelectedDepartureDate) {
			const durationMax = maxBy(
				durationsOfSelectedDepartureDate,
				duration => duration?.duration
			);

			calendarMaxDate = add(selectedDepartureDate, {
				days: durationMax?.duration,
			});
		} else {
			const availableDates = Object.keys(durationByAvailableDate);
			calendarMaxDate = max(availableDates);
		}
	}

	const maxSelectableDate = addDays(new Date(selectedDepartureDate), MAX_BOOKING_DATE_OFFSET);

	if (selectedDepartureDate && !selectedEndDate && isCalendarAvailableDateError) {
		calendarMaxDate = format(maxSelectableDate, SDP_ROTATION_DATE_FORMAT);
	}

	const isCalendarDisabled = isEmpty(durationByAvailableDate) && !isCalendarAvailableDateError;

	const clearTravelDatesFields = () => {
		setFieldValue("travelDates.departureDate", undefined);
		setFieldValue("travelDates.endDate", undefined);
		setFieldValue("duration", undefined);
		setFieldTouched("travelDates", false);
	};

	// get durations when the value of departureDate change
	// and verify if selectedDepartureDate is available
	useEffect(() => {
		if (selectedDepartureDate && !isEmpty(durationByAvailableDate)) {
			const formattedSelectedDepartureDate = format(
				selectedDepartureDate,
				SDP_ROTATION_DATE_FORMAT
			);

			// update duration from selected departureDate
			const durationsOfSelectedDepartureDate =
				durationByAvailableDate[formattedSelectedDepartureDate];

			handleDayChange({
				durationsOfSelectedDepartureDate,
				selectedEndDate,
				selectedDepartureDate,
			});

			// check is selected departure date is available
			const filteredDurationByAvailableDate = Object.keys(durationByAvailableDate).filter(
				date => {
					return isWithinInterval(new Date(date), {
						start: new Date(calendarMinDate),
						end: new Date(calendarMaxDate),
					});
				}
			);

			// if not, clear departure date, end Date and duration fields
			if (!filteredDurationByAvailableDate.includes(formattedSelectedDepartureDate)) {
				clearTravelDatesFields();
			}

			if (selectedEndDate) {
				const formattedSelectedEndDate =
					selectedEndDate && format(selectedEndDate, SDP_ROTATION_DATE_FORMAT);

				// check if selectedEndDate is available
				const dataOfSelectedDepartureDateMatchingSelectedEndDate = durationsOfSelectedDepartureDate?.find(
					data => {
						return data.ed === formattedSelectedEndDate;
					}
				);
				// if not clear departure date, end Date and duration fields
				if (!dataOfSelectedDepartureDateMatchingSelectedEndDate) {
					clearTravelDatesFields();
				}
			}
		} else if (
			selectedDepartureDate &&
			selectedEndDate &&
			isEmpty(durationByAvailableDate) &&
			isCalendarAvailableDateError
		) {
			handleDayChange({
				durationsOfSelectedDepartureDate: [],
				selectedEndDate,
				selectedDepartureDate,
			});
		}
	}, [
		durationByAvailableDate,
		selectedDepartureDate,
		selectedEndDate,
		isCalendarAvailableDateError,
	]);

	useEffect(() => {
		if (uri) {
			fetchSDPAvailableDepartureDate(uri)
				// eslint-disable-next-line consistent-return
				.then(response => {
					const { status: responseStatus } = response || {};
					if (responseStatus !== HTTP_STATUS_CODES.OK) {
						setIsCalendarAvailableDateError(true);
						return null;
					}
				})
				.catch(() => {
					setIsCalendarAvailableDateError(true);
				});
		}
	}, [uri, isCalendarAvailableDateError]);

	const checkDateAvailability = useCallback(
		date => {
			const hasDurations = !isEmpty(durationByAvailableDate);
			const hasError = isCalendarAvailableDateError;

			// Si aucune date n'est fournie ou s'il n'y a pas de données disponibles et pas d'erreur on desactive la date
			if (!date || (!hasDurations && !hasError)) {
				return true;
			}

			// Cas sans erreur avec des données disponibles
			if (hasDurations && !hasError) {
				const currentDateKey = format(new Date(date), SDP_ROTATION_DATE_FORMAT);

				if (selectedDepartureDate && !selectedEndDate) {
					const departureKey = format(
						new Date(selectedDepartureDate),
						SDP_ROTATION_DATE_FORMAT
					);
					const availableData = durationByAvailableDate[departureKey] || [];

					return !availableData.some(item => item.ed === currentDateKey);
				}

				return !Object.keys(durationByAvailableDate).includes(currentDateKey);
			}

			// Cas avec erreur et sans données disponibles
			if (!hasDurations && hasError && selectedDepartureDate && !selectedEndDate) {
				const dateTime = new Date(date).getTime();
				const minDate = addDays(
					new Date(selectedDepartureDate),
					MIN_BOOKING_DATE_OFFSET
				).getTime();
				const maxDate = maxSelectableDate.getTime();

				return !(dateTime >= minDate && dateTime <= maxDate);
			}

			return false;
		},
		[
			durationByAvailableDate,
			selectedDepartureDate,
			selectedEndDate,
			isCalendarAvailableDateError,
		]
	);

	const handleCalendarClose = useCallback(() => {
		if (selectedDepartureDate && !selectedEndDate) {
			clearTravelDatesFields();
		}
	}, [selectedDepartureDate, selectedEndDate]);

	return isMobile ? (
		<DateCalendarDrawerInput
			{...props}
			departureDateMin={calendarMinDate}
			departureDateMax={calendarMaxDate}
			checkDateAvailability={checkDateAvailability}
			onClose={handleCalendarClose}
			handleDayChange={handleDayChange}
			isCalendarDisabled={isCalendarDisabled}
			popperOffset={popperOffset}
		/>
	) : (
		<DateCalendarInput
			{...props}
			departureDateMin={calendarMinDate}
			departureDateMax={calendarMaxDate}
			updateFloatingPositionReference={updateFloatingPositionReference}
			checkDateAvailability={checkDateAvailability}
			handleDayChange={handleDayChange}
			isCalendarDisabled={isCalendarDisabled}
			popperOffset={popperOffset}
		/>
	);
};

SmartDPDateCalendarContainer.propTypes = {
	uri: PropTypes.string,
	updateFloatingPositionReference: PropTypes.func,
	fetchSDPAvailableDepartureDate: PropTypes.func,
	handleDayChange: PropTypes.func,
	popperOffset: PropTypes.array,
	durationByAvailableDate: PropTypes.object,
};

const mapStateToProps = state => ({
	durationByAvailableDate: state.smartDP.availabilitiesDate,
});

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchSDPAvailableDepartureDate,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(SmartDPDateCalendarContainer);
